import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { useIsMobile } from "../hooks/useIsMobile";
import { GooglePlayBadge, AppStoreBadge } from "../components/StoreBadges";

const LOAD_STEPS = [
  "Starting Engines...",
  "Connecting FASTag Network...",
  "Loading Fleet Intelligence...",
  "Ready To Go",
];

const CSS = `
@keyframes truckRoll {
  from { transform: translateX(-220px); }
  to   { transform: translateX(calc(100vw + 220px)); }
}
@keyframes wheelSpin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}
@keyframes cloudDrift1 {
  from { transform: translateX(-300px); }
  to   { transform: translateX(calc(100vw + 300px)); }
}
@keyframes cloudDrift2 {
  from { transform: translateX(-200px); }
  to   { transform: translateX(calc(100vw + 200px)); }
}
@keyframes birdFly {
  0%   { transform: translateX(-60px) translateY(0px) scaleX(1); }
  25%  { transform: translateX(calc(25vw)) translateY(-18px) scaleX(1); }
  50%  { transform: translateX(calc(50vw)) translateY(0px) scaleX(1); }
  75%  { transform: translateX(calc(75vw)) translateY(-12px) scaleX(1); }
  100% { transform: translateX(calc(100vw + 60px)) translateY(0px) scaleX(1); }
}
@keyframes birdWing {
  0%,100% { d: path("M0,0 Q-6,-4 -10,-1 Q-6,3 0,0 Q6,-4 10,-1 Q6,3 0,0"); }
  50%      { d: path("M0,0 Q-6,2 -10,5 Q-6,3 0,0 Q6,2 10,5 Q6,3 0,0"); }
}
@keyframes treeWave {
  0%,100% { transform: rotate(0deg); transform-origin: bottom center; }
  25%     { transform: rotate(1.5deg); transform-origin: bottom center; }
  75%     { transform: rotate(-1.5deg); transform-origin: bottom center; }
}
@keyframes sunRise {
  from { transform: translateY(30px); opacity:0.6; }
  to   { transform: translateY(0px); opacity:1; }
}
@keyframes dustPuff {
  0%   { transform: translateX(0) scale(0.4); opacity:0.55; }
  100% { transform: translateX(-28px) scale(1.6); opacity:0; }
}
@keyframes progressGrow {
  from { width: 0%; }
  to   { width: 100%; }
}
@keyframes miniTruck {
  from { left: 0%; }
  to   { left: calc(100% - 40px); }
}
@keyframes grassWave {
  0%,100% { transform: scaleX(1) scaleY(1); }
  50%     { transform: scaleX(1.008) scaleY(1.015); }
}
@keyframes headlightPulse {
  0%,100% { opacity:0.7; }
  50%     { opacity:1; }
}
@keyframes exhaust {
  0%   { transform: translate(0,0) scale(0.5); opacity:0.45; }
  100% { transform: translate(-20px,-10px) scale(1.8); opacity:0; }
}
@keyframes suspenseBounce {
  0%,100% { transform: translateY(0); }
  50%     { transform: translateY(-2px); }
}
@keyframes statFadeUp {
  from { opacity:0; transform:translateY(16px); }
  to   { opacity:1; transform:translateY(0); }
}
@keyframes glassFloat {
  0%,100% { transform: translateY(0px); }
  50%      { transform: translateY(-6px); }
}
@keyframes glassGlow {
  0%,100% { box-shadow: 0 8px 32px rgba(99,102,241,0.18), 0 0 0 1px rgba(139,92,246,0.18), inset 0 1px 0 rgba(255,255,255,0.07); }
  50%      { box-shadow: 0 16px 48px rgba(99,102,241,0.38), 0 0 0 1px rgba(139,92,246,0.36), inset 0 1px 0 rgba(255,255,255,0.13); }
}
@media (max-width: 1100px) {
  .lk-glass-panel { display: none !important; }
}
@media (max-width: 640px) {
  .lk-stats-row { display: grid !important; grid-template-columns: 1fr 1fr; gap: 8px !important; }
  .lk-stat-card { max-width: none !important; flex: none !important; }
  .lk-hero-top { top: max(8%, calc(var(--sat, 0px) + 8px)) !important; }
  .lk-load-bar { padding-bottom: max(68px, calc(var(--sab, 0px) + 64px)) !important; }
}
@media (max-width: 1100px) {
  .lk-glass-panel { display: none !important; }
}
`;

function TruckSVG({ style }) {
  return (
    <svg viewBox="0 0 200 80" width="200" height="80" style={style}>
      {/* Shadow */}
      <ellipse cx="100" cy="76" rx="76" ry="5" fill="rgba(0,0,0,0.18)" />

      {/* Trailer body */}
      <rect x="10" y="22" width="118" height="42" rx="4" fill="#4F46E5" />
      <rect x="12" y="24" width="114" height="38" rx="3" fill="#6366F1" />
      {/* Trailer stripes */}
      <rect x="12" y="24" width="114" height="6" rx="2" fill="#818CF8" opacity="0.5" />
      <line x1="40" y1="24" x2="40" y2="62" stroke="#4338CA" strokeWidth="1" opacity="0.4" />
      <line x1="70" y1="24" x2="70" y2="62" stroke="#4338CA" strokeWidth="1" opacity="0.4" />
      <line x1="100" y1="24" x2="100" y2="62" stroke="#4338CA" strokeWidth="1" opacity="0.4" />
      {/* LORRYKHOJO text on trailer */}
      <text x="62" y="48" textAnchor="middle" fontSize="7" fontWeight="800"
        fill="white" fontFamily="Arial,sans-serif" letterSpacing="1" opacity="0.9">LORRYKHOJO</text>

      {/* Cab */}
      <rect x="128" y="30" width="52" height="34" rx="5" fill="#3730A3" />
      {/* Cab top */}
      <path d="M130,30 Q134,16 150,14 L176,14 L180,30 Z" fill="#312E81" />
      {/* Cab roof air scoop */}
      <rect x="152" y="10" width="22" height="6" rx="3" fill="#4338CA" />

      {/* Windshield */}
      <path d="M132,29 Q135,17 150,15 L174,15 L178,29 Z" fill="#A5B4FC" opacity="0.85" />
      {/* Windshield glare */}
      <path d="M136,27 Q138,19 148,17 L155,17 L158,27 Z" fill="white" opacity="0.25" />

      {/* Side window */}
      <rect x="152" y="33" width="22" height="16" rx="2" fill="#818CF8" opacity="0.65" />

      {/* Door lines */}
      <line x1="155" y1="32" x2="155" y2="63" stroke="#312E81" strokeWidth="0.8" />
      {/* Door handle */}
      <rect x="163" y="46" width="8" height="2.5" rx="1" fill="#6366F1" />

      {/* Headlight */}
      <rect x="177" y="36" width="10" height="7" rx="2" fill="#FEF3C7" style={{ animation:"headlightPulse 1.2s ease-in-out infinite" }} />
      <path d="M187,37 L200,32 L200,46 L187,43 Z" fill="#FEF9C3" opacity="0.35" style={{ animation:"headlightPulse 1.2s ease-in-out infinite" }} />

      {/* Front grill */}
      <rect x="177" y="44" width="10" height="10" rx="1" fill="#312E81" />
      <line x1="179" y1="46" x2="185" y2="46" stroke="#4338CA" strokeWidth="0.8" />
      <line x1="179" y1="49" x2="185" y2="49" stroke="#4338CA" strokeWidth="0.8" />
      <line x1="179" y1="52" x2="185" y2="52" stroke="#4338CA" strokeWidth="0.8" />

      {/* Bumper */}
      <rect x="178" y="54" width="10" height="5" rx="1" fill="#1E1B4B" />

      {/* Connector */}
      <rect x="124" y="48" width="8" height="6" rx="1" fill="#2E2761" />

      {/* Rear lights on trailer */}
      <rect x="10" y="32" width="5" height="8" rx="1" fill="#FCA5A5" opacity="0.9" />
      <rect x="10" y="44" width="5" height="8" rx="1" fill="#F87171" opacity="0.9" />

      {/* Front wheel */}
      <g style={{ transformOrigin:"162px 67px", animation:"wheelSpin 0.6s linear infinite" }}>
        <circle cx="162" cy="67" r="10" fill="#1E1B4B" />
        <circle cx="162" cy="67" r="7" fill="#312E81" />
        <circle cx="162" cy="67" r="2.5" fill="#6366F1" />
        <line x1="162" y1="57" x2="162" y2="77" stroke="#4338CA" strokeWidth="1.5" />
        <line x1="152" y1="67" x2="172" y2="67" stroke="#4338CA" strokeWidth="1.5" />
      </g>

      {/* Rear wheel pair 1 */}
      <g style={{ transformOrigin:"45px 67px", animation:"wheelSpin 0.6s linear infinite" }}>
        <circle cx="45" cy="67" r="10" fill="#1E1B4B" />
        <circle cx="45" cy="67" r="7" fill="#312E81" />
        <circle cx="45" cy="67" r="2.5" fill="#6366F1" />
        <line x1="45" y1="57" x2="45" y2="77" stroke="#4338CA" strokeWidth="1.5" />
        <line x1="35" y1="67" x2="55" y2="67" stroke="#4338CA" strokeWidth="1.5" />
      </g>

      {/* Rear wheel pair 2 */}
      <g style={{ transformOrigin:"72px 67px", animation:"wheelSpin 0.6s linear infinite" }}>
        <circle cx="72" cy="67" r="10" fill="#1E1B4B" />
        <circle cx="72" cy="67" r="7" fill="#312E81" />
        <circle cx="72" cy="67" r="2.5" fill="#6366F1" />
        <line x1="72" y1="57" x2="72" y2="77" stroke="#4338CA" strokeWidth="1.5" />
        <line x1="62" y1="67" x2="82" y2="67" stroke="#4338CA" strokeWidth="1.5" />
      </g>

      {/* Dust particles */}
      <circle cx="28" cy="65" r="4" fill="#D1D5DB"
        style={{ transformOrigin:"28px 65px", animation:"dustPuff 0.8s ease-out infinite" }} />
      <circle cx="22" cy="62" r="2.5" fill="#E5E7EB"
        style={{ transformOrigin:"22px 62px", animation:"dustPuff 0.8s 0.2s ease-out infinite" }} />
    </svg>
  );
}

function Cloud({ x, y, scale = 1, duration = 28, delay = 0, opacity = 0.9 }) {
  return (
    <g style={{ transform:`translateX(${x}px) translateY(${y}px) scale(${scale})`,
      animation:`cloudDrift1 ${duration}s ${delay}s linear infinite`, opacity }}>
      <ellipse cx="0" cy="0" rx="38" ry="18" fill="white" />
      <ellipse cx="-20" cy="4" rx="22" ry="14" fill="white" />
      <ellipse cx="22" cy="4" rx="26" ry="14" fill="white" />
      <ellipse cx="0" cy="8" rx="30" ry="12" fill="white" />
    </g>
  );
}

function Bird({ x, y, scale = 1, duration = 12, delay = 0 }) {
  return (
    <g style={{ transform:`translateX(${x}px) translateY(${y}px) scale(${scale})`,
      animation:`birdFly ${duration}s ${delay}s linear infinite` }}>
      <path d="M0,0 Q-5,-3 -9,0" stroke="#374151" strokeWidth="1.2" fill="none" strokeLinecap="round" />
      <path d="M0,0 Q5,-3 9,0" stroke="#374151" strokeWidth="1.2" fill="none" strokeLinecap="round" />
    </g>
  );
}

function Tree({ x, height = 60, color = "#166534" }) {
  return (
    <g style={{ transform:`translateX(${x}px)`, animation:"treeWave 3s ease-in-out infinite" }}>
      <rect x="-4" y={height * 0.55} width="8" height={height * 0.45} fill="#92400E" rx="1" />
      <polygon points={`0,0 ${-height*0.35},${height*0.65} ${height*0.35},${height*0.65}`} fill={color} />
      <polygon points={`0,${height*0.22} ${-height*0.38},${height*0.78} ${height*0.38},${height*0.78}`} fill={color} opacity="0.9" />
    </g>
  );
}

function IconSatellite() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#A5B4FC" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4.5 16.5l3-3" />
      <path d="M9 6l9 9" />
      <path d="M7.5 4.5l3 3-4.5 4.5-3-3z" />
      <path d="M13.5 10.5l3 3-4.5 4.5-3-3z" />
      <path d="M14 20c3.314 0 6-2.686 6-6" />
      <path d="M14 16c1.657 0 3-1.343 3-3" />
    </svg>
  );
}

function IconRoad() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#A5B4FC" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 17l4-10h10l4 10H3z" />
      <line x1="12" y1="7" x2="12" y2="17" />
      <line x1="8" y1="17" x2="7" y2="17" />
      <line x1="16" y1="17" x2="17" y2="17" />
    </svg>
  );
}

function IconBarChart() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#A5B4FC" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3"  y="12" width="4" height="9" rx="1" />
      <rect x="10" y="7"  width="4" height="14" rx="1" />
      <rect x="17" y="3"  width="4" height="18" rx="1" />
    </svg>
  );
}

function IconBell() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#A5B4FC" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  );
}

const STATS = [
  { label: "Live Tracking",       Icon: IconSatellite, value: "Real-Time"    },
  { label: "FASTag Intelligence", Icon: IconRoad,       value: "NETC Powered" },
  { label: "Fleet Analytics",     Icon: IconBarChart,   value: "Smart Insights" },
  { label: "Smart Alerts",        Icon: IconBell,       value: "Instant Notify" },
];

// ── GLASS NAV PANELS ─────────────────────────────────────────────────────────

const LEFT_LINKS = [
  { href: "/pricing", label: "Pricing",    icon: (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
    </svg>
  )},
  { href: "/contact", label: "Contact Us", icon: (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
    </svg>
  )},
  { href: "/terms",   label: "Terms",      icon: (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
    </svg>
  )},
];

const RIGHT_LINKS = [
  { href: "/privacy",  label: "Privacy Policy",  icon: (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    </svg>
  )},
  { href: "/refund",   label: "Refund Policy",   icon: (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-3.81"/>
    </svg>
  )},
];

function GlassPanelLink({ href, label, icon, animDelay }) {
  const [hovered, setHovered] = useState(false);
  return (
    <motion.a
      href={href}
      initial={{ opacity: 0, y: 6, filter: "blur(3px)" }}
      animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
      transition={{ duration: 0.45, delay: animDelay, ease: "easeOut" }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "flex", alignItems: "center", gap: 9,
        padding: "9px 11px", borderRadius: 10,
        textDecoration: "none",
        color: hovered ? "#fff" : "rgba(255,255,255,0.62)",
        background: hovered ? "rgba(139,92,246,0.18)" : "transparent",
        fontSize: 12.5, fontWeight: 500, letterSpacing: 0.15,
        transition: "color 0.2s, background 0.2s",
        cursor: "pointer",
        userSelect: "none",
        WebkitUserSelect: "none",
      }}
    >
      <span style={{
        color: hovered ? "#C4B5FD" : "#A5B4FC",
        transform: hovered ? "translateX(3px)" : "translateX(0px)",
        transition: "transform 0.22s ease, color 0.2s",
        flexShrink: 0, display: "flex", alignItems: "center",
      }}>
        {icon}
      </span>
      <span style={{ position: "relative" }}>
        {label}
        <span style={{
          position: "absolute", bottom: -1, left: 0,
          height: "1px", borderRadius: 1,
          background: "linear-gradient(90deg, #8B5CF6, #6366F1)",
          width: hovered ? "100%" : "0%",
          transition: "width 0.25s ease",
          display: "block",
        }} />
      </span>
    </motion.a>
  );
}

function GlassPanel({ links, side, panelDelay }) {
  return (
    <div style={{
      position: "absolute",
      top: "56%",
      transform: "translateY(-50%)",
      [side]: "clamp(14px, 2vw, 32px)",
      zIndex: 20,
      pointerEvents: "auto",
      width: "clamp(148px, 13vw, 182px)",
    }} className="lk-glass-panel">
      <motion.div
        initial={{ opacity: 0, y: 28, filter: "blur(10px)" }}
        animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
        transition={{ duration: 0.8, delay: panelDelay, ease: [0.23, 1, 0.32, 1] }}
      >
        <div style={{
          background: "rgba(10,14,40,0.58)",
          backdropFilter: "blur(22px)",
          WebkitBackdropFilter: "blur(22px)",
          border: "1px solid rgba(139,92,246,0.28)",
          borderRadius: 18,
          padding: "10px 8px",
          animation: "glassFloat 4.5s ease-in-out infinite, glassGlow 3.8s ease-in-out infinite",
        }}>
          {links.map(({ href, label, icon }, i) => (
            <GlassPanelLink
              key={href}
              href={href}
              label={label}
              icon={icon}
              animDelay={panelDelay + 0.18 + i * 0.11}
            />
          ))}
        </div>
      </motion.div>
    </div>
  );
}

function StoreBadgeCorner({ side, children, panelDelay }) {
  return (
    <div style={{
      position: "absolute",
      bottom: "clamp(14px, 3vw, 28px)",
      [side]: "clamp(14px, 2vw, 32px)",
      zIndex: 20,
      pointerEvents: "auto",
    }} className="lk-glass-panel">
      <motion.div
        initial={{ opacity: 0, y: 20, filter: "blur(10px)" }}
        animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
        transition={{ duration: 0.8, delay: panelDelay, ease: [0.23, 1, 0.32, 1] }}
      >
        {children}
      </motion.div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

export default function LandingPage() {
  const navigate        = useNavigate();
  useAuth();
  const isMobile        = useIsMobile();
  const [step, setStep] = useState(0);
  const [done, setDone] = useState(false);
  const timerRef        = useRef(null);
  const navRef          = useRef(navigate);

  // Keep navRef current without adding navigate to effect deps
  useEffect(() => { navRef.current = navigate; }, [navigate]);


  useEffect(() => {
    // Advance loading steps every ~1.1 s → total ~4.4 s
    let i = 0;
    timerRef.current = setInterval(() => {
      i++;
      if (i < LOAD_STEPS.length) {
        setStep(i);
      } else {
        clearInterval(timerRef.current);
        setDone(true);
      }
    }, 1100);
    return () => clearInterval(timerRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const progress = Math.min(100, ((step + 1) / LOAD_STEPS.length) * 100);

  return (
    <>
      <style>{CSS}</style>
      <div style={{
        position: "fixed", inset: 0, overflow: "hidden",
        fontFamily: "-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
      }}>

        {/* ── SAFE AREA MASK — hides cloud animation behind camera/notch zone ── */}
        {isMobile && (
          <div style={{
            position: "fixed", top: 0, left: 0, right: 0,
            height: "var(--sat, 0px)",
            background: "#0F172A",
            zIndex: 200, pointerEvents: "none",
          }} />
        )}

        {/* ── SKY ── */}
        <div style={{
          position: "absolute", inset: 0,
          background: "linear-gradient(180deg, #0F172A 0%, #1E1B4B 18%, #312E81 35%, #7C3AED 52%, #DB2777 68%, #F97316 82%, #FCD34D 100%)",
        }} />

        {/* ── SUN ── */}
        <div style={{
          position: "absolute", left: "62%", top: "12%",
          animation: "sunRise 2s ease-out both",
        }}>
          <svg width="90" height="90" viewBox="0 0 90 90">
            <defs>
              <radialGradient id="sunGrad" cx="50%" cy="50%" r="50%">
                <stop offset="0%"   stopColor="#FEF9C3" />
                <stop offset="40%"  stopColor="#FCD34D" />
                <stop offset="100%" stopColor="#F97316" stopOpacity="0" />
              </radialGradient>
            </defs>
            <circle cx="45" cy="45" r="40" fill="url(#sunGrad)" opacity="0.55" />
            <circle cx="45" cy="45" r="20" fill="#FCD34D" />
            <circle cx="45" cy="45" r="16" fill="#FEF3C7" />
          </svg>
        </div>

        {/* ── CLOUDS SVG LAYER ── */}
        <svg style={{ position:"absolute", top:0, left:0, width:"100%", height:"40%", overflow:"visible" }}
          viewBox="0 0 1440 260" preserveAspectRatio="xMidYMid slice">
          <Cloud x={-300} y={60}  scale={1.2} duration={32} delay={0}  opacity={0.85} />
          <Cloud x={-600} y={40}  scale={0.8} duration={26} delay={5}  opacity={0.7}  />
          <Cloud x={-900} y={90}  scale={1}   duration={38} delay={12} opacity={0.75} />
          <Cloud x={-200} y={30}  scale={0.6} duration={22} delay={8}  opacity={0.6}  />
          <Cloud x={-500} y={110} scale={1.4} duration={44} delay={3}  opacity={0.5}  />
        </svg>

        {/* ── BIRDS ── */}
        <svg style={{ position:"absolute", top:0, left:0, width:"100%", height:"50%", overflow:"visible" }}
          viewBox="0 0 1440 300" preserveAspectRatio="none">
          <Bird x={-60}  y={80}  scale={1.2} duration={14} delay={2}  />
          <Bird x={-80}  y={68}  scale={0.8} duration={16} delay={4}  />
          <Bird x={-40}  y={95}  scale={1}   duration={18} delay={7}  />
          <Bird x={-100} y={55}  scale={0.7} duration={12} delay={10} />
          <Bird x={-60}  y={110} scale={0.9} duration={20} delay={1}  />
        </svg>

        {/* ── MOUNTAINS LAYER 1 (far) ── */}
        <svg style={{ position:"absolute", bottom:"28%", left:0, width:"100%", overflow:"visible" }}
          viewBox="0 0 1440 200" preserveAspectRatio="none">
          <defs>
            <linearGradient id="mtn1" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"   stopColor="#1E1B4B" />
              <stop offset="100%" stopColor="#312E81" />
            </linearGradient>
          </defs>
          <polygon points="0,200 120,40 280,120 440,20 600,100 760,30 920,90 1080,15 1240,80 1440,25 1440,200"
            fill="url(#mtn1)" opacity="0.9" />
        </svg>

        {/* ── MOUNTAINS LAYER 2 (near) ── */}
        <svg style={{ position:"absolute", bottom:"22%", left:0, width:"100%", overflow:"visible" }}
          viewBox="0 0 1440 160" preserveAspectRatio="none">
          <defs>
            <linearGradient id="mtn2" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"   stopColor="#312E81" />
              <stop offset="100%" stopColor="#1E1B4B" />
            </linearGradient>
          </defs>
          <polygon points="0,160 180,50 360,110 520,30 700,90 880,20 1060,70 1250,10 1440,55 1440,160"
            fill="url(#mtn2)" />
        </svg>

        {/* ── GROUND / GRASS ── */}
        <div style={{
          position: "absolute", bottom: 0, left: 0, right: 0, height: "26%",
          background: "linear-gradient(180deg,#14532D 0%,#166534 30%,#15803D 100%)",
          animation: "grassWave 4s ease-in-out infinite",
        }} />

        {/* ── TREES ── */}
        <svg style={{ position:"absolute", bottom:"22%", left:0, width:"100%", height:"18%", overflow:"visible" }}
          viewBox="0 0 1440 100" preserveAspectRatio="none">
          {[80,180,300,420,560,680,800,950,1100,1250,1380].map((x, i) => (
            <Tree key={x} x={x} height={55 + (i % 3) * 12} color={i % 2 === 0 ? "#15803D" : "#166534"} />
          ))}
        </svg>

        {/* ── ROAD ── */}
        <div style={{
          position: "absolute", bottom: "18%", left: 0, right: 0, height: "8%",
          background: "#374151",
        }}>
          <div style={{
            position: "absolute", top: "50%", left: 0, right: 0, height: 3,
            background: "repeating-linear-gradient(90deg, #FCD34D 0, #FCD34D 40px, transparent 40px, transparent 80px)",
            opacity: 0.7,
          }} />
        </div>

        {/* ── TRUCK ── */}
        <div style={{
          position: "absolute",
          bottom: "calc(18% + 2px)",
          left: 0, width: "100%",
          animation: "suspenseBounce 0.4s ease-in-out infinite",
        }}>
          <div style={{ animation: "truckRoll 7s linear infinite" }}>
            <TruckSVG style={{ display:"block" }} />
          </div>
        </div>

        {/* ── HERO TEXT (center) ── */}
        <div className="lk-hero-top" style={{
          position: "absolute", top: "8%", left: 0, right: 0,
          display: "flex", flexDirection: "column", alignItems: "center",
          padding: "0 16px", pointerEvents: "none",
        }}>
          {/* Logo */}
          <motion.div
            initial={{ opacity:0, y:-20 }}
            animate={{ opacity:1, y:0 }}
            transition={{ duration:0.7, delay:0.1 }}
            style={{ display:"flex", alignItems:"center", gap:10, marginBottom:20 }}
          >
            <div style={{
              width:40, height:40, borderRadius:12,
              background:"linear-gradient(135deg,#6366F1,#8B5CF6)",
              display:"flex", alignItems:"center", justifyContent:"center",
              boxShadow:"0 4px 16px rgba(99,102,241,0.5)",
            }}>
              <svg width="20" height="20" viewBox="0 0 16 16" fill="none">
                <path d="M8 2L14 5.5V10.5L8 14L2 10.5V5.5L8 2Z" stroke="#fff" strokeWidth="1.5" fill="none" strokeLinejoin="round"/>
                <circle cx="8" cy="8" r="2" fill="#fff"/>
              </svg>
            </div>
            <span style={{ fontSize:18, fontWeight:800, color:"#fff", letterSpacing:1.5 }}>LORRYKHOJO</span>
          </motion.div>

          <motion.h1
            initial={{ opacity:0, y:20 }}
            animate={{ opacity:1, y:0 }}
            transition={{ duration:0.8, delay:0.3 }}
            style={{
              fontSize: "clamp(28px, 6vw, 64px)",
              fontWeight: 900, color: "#fff",
              textAlign: "center", lineHeight: 1.15,
              letterSpacing: -1, margin: "0 0 14px",
              textShadow: "0 2px 24px rgba(0,0,0,0.5)",
            }}
          >
            Track Every Mile.<br />
            <span style={{ background:"linear-gradient(90deg,#FCD34D,#F97316)", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent" }}>
              Deliver Every Time.
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity:0, y:16 }}
            animate={{ opacity:1, y:0 }}
            transition={{ duration:0.7, delay:0.55 }}
            style={{
              fontSize: "clamp(13px, 2vw, 18px)",
              color: "rgba(255,255,255,0.8)",
              textAlign: "center", maxWidth: 520, margin: "0 0 28px",
              lineHeight: 1.6,
            }}
          >
            India's smartest FASTag-powered fleet intelligence platform.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity:0, y:14 }}
            animate={{ opacity:1, y:0 }}
            transition={{ duration:0.7, delay:0.75 }}
            style={{ display:"flex", gap:12, flexWrap:"wrap", justifyContent:"center" }}
          >
            <button
              onClick={() => navigate("/auth")}
              style={{
                height:46, padding:"0 28px", borderRadius:12,
                background:"linear-gradient(135deg,#6366F1,#8B5CF6)",
                border:"none", color:"#fff", fontSize:15, fontWeight:700,
                cursor:"pointer", pointerEvents:"auto",
                boxShadow:"0 4px 20px rgba(99,102,241,0.55)",
                transition:"transform 0.15s,box-shadow 0.15s",
              }}
              onMouseEnter={e => { e.currentTarget.style.transform="translateY(-2px)"; e.currentTarget.style.boxShadow="0 8px 28px rgba(99,102,241,0.65)"; }}
              onMouseLeave={e => { e.currentTarget.style.transform="translateY(0)"; e.currentTarget.style.boxShadow="0 4px 20px rgba(99,102,241,0.55)"; }}
            >
              Get Started →
            </button>
            <button
              onClick={() => navigate("/demo")}
              style={{
                height:46, padding:"0 28px", borderRadius:12,
                background:"rgba(255,255,255,0.12)", backdropFilter:"blur(8px)",
                border:"1px solid rgba(255,255,255,0.3)", color:"#fff",
                fontSize:15, fontWeight:600, cursor:"pointer", pointerEvents:"auto",
                transition:"background 0.15s,transform 0.15s",
              }}
              onMouseEnter={e => { e.currentTarget.style.background="rgba(255,255,255,0.2)"; e.currentTarget.style.transform="translateY(-2px)"; }}
              onMouseLeave={e => { e.currentTarget.style.background="rgba(255,255,255,0.12)"; e.currentTarget.style.transform="translateY(0)"; }}
            >
              Watch Demo
            </button>
          </motion.div>
        </div>

        {/* ── BRAND CREDIT ── */}
        <div style={{
          position: "absolute", bottom: 0, left: 0, right: 0,
          paddingBottom: "max(6px, var(--sab, 0px))",
          paddingTop: 6,
          textAlign: "center", fontSize: 11, color: "rgba(255,255,255,0.3)",
          letterSpacing: 0.3, pointerEvents: "none", zIndex: 10, userSelect: "none",
        }}>
          Made with{" "}<span style={{ color: "#F87171" }}>❤</span>{" "}in India by{" "}
          <a
            href="https://gvalabs.in/"
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontWeight: 700, color: "rgba(255,255,255,0.55)", textDecoration: "none", pointerEvents: "auto" }}
            onMouseEnter={e => e.currentTarget.style.color = "#818CF8"}
            onMouseLeave={e => e.currentTarget.style.color = "rgba(255,255,255,0.55)"}
          >
            GVALabs
          </a>
        </div>

        {/* ── GLASS NAV PANELS — desktop only ── */}
        {!isMobile && (
          <>
            <GlassPanel links={LEFT_LINKS}  side="left"  panelDelay={1.2} />
            <GlassPanel links={RIGHT_LINKS} side="right" panelDelay={1.4} />
            <StoreBadgeCorner side="left"  panelDelay={1.6}><AppStoreBadge /></StoreBadgeCorner>
            <StoreBadgeCorner side="right" panelDelay={1.7}><GooglePlayBadge /></StoreBadgeCorner>
          </>
        )}

        {/* ── MOBILE BOTTOM NAV — fixed tab bar, mobile only ── */}
        {isMobile && (
          <div style={{
            position: "fixed",
            bottom: 0, left: 0, right: 0,
            paddingBottom: "var(--sab, 0px)",
            zIndex: 50,
            background: "rgba(10,14,40,0.95)",
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            borderTop: "1px solid rgba(139,92,246,0.35)",
            display: "flex",
            flexDirection: "row",
            alignItems: "stretch",
          }}>
            {[...LEFT_LINKS, ...RIGHT_LINKS].map(({ href, label, icon }) => (
              <a
                key={href}
                href={href}
                style={{
                  flex: 1,
                  display: "flex", flexDirection: "column",
                  alignItems: "center", justifyContent: "center",
                  gap: 4, padding: "8px 2px",
                  textDecoration: "none",
                  color: "rgba(255,255,255,0.55)",
                  fontSize: 9, fontWeight: 500, letterSpacing: 0.2,
                  minHeight: 48, textAlign: "center",
                  borderRight: "1px solid rgba(255,255,255,0.04)",
                  transition: "color 0.15s, background 0.15s",
                }}
              >
                <span style={{ color: "#A5B4FC", display: "flex", marginBottom: 1 }}>{icon}</span>
                <span style={{ lineHeight: 1.2 }}>{label}</span>
              </a>
            ))}
          </div>
        )}

        {/* ── STATS ROW ── */}
        <div className="lk-stats-row" style={{
          position: "absolute",
          ...(isMobile
            ? { top: "calc(8% + 278px)" }
            : { bottom: "calc(26% + 16px)" }),
          left: 0, right: 0,
          display: "flex", justifyContent: "center",
          gap: "clamp(8px, 2vw, 24px)",
          padding: "0 16px",
          flexWrap: "wrap",
        }}>
          {STATS.map((s, i) => (
            <motion.div
              key={s.label}
              className="lk-stat-card"
              initial={{ opacity:0, y:20 }}
              animate={{ opacity:1, y:0 }}
              transition={{ duration:0.5, delay:0.9 + i * 0.12 }}
              style={{
                background: "rgba(255,255,255,0.1)",
                backdropFilter: "blur(12px)",
                border: "1px solid rgba(255,255,255,0.2)",
                borderRadius: 12, padding: "8px 12px",
                display: "flex", alignItems: "center", gap: 8,
                flex: "1 1 120px", maxWidth: 180,
              }}
            >
              <s.Icon />
              <div>
                <div style={{ fontSize:10, color:"rgba(255,255,255,0.6)", fontWeight:500, letterSpacing:0.5 }}>{s.label}</div>
                <div style={{ fontSize:12, color:"#fff", fontWeight:700 }}>{s.value}</div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* ── LOADING BAR ── */}
        <AnimatePresence>
          {!done && (
            <motion.div
              initial={{ opacity:0, y:20 }}
              animate={{ opacity:1, y:0 }}
              exit={{ opacity:0, y:10 }}
              transition={{ duration:0.4, delay:0.2 }}
              className="lk-load-bar"
              style={{
                position: "absolute", bottom: 0, left: 0, right: 0,
                padding: "20px 24px 24px",
                background: "linear-gradient(180deg, transparent, rgba(0,0,0,0.7))",
              }}
            >
              {/* Step label */}
              <div style={{
                textAlign:"center", fontSize:12, color:"rgba(255,255,255,0.75)",
                fontWeight:600, letterSpacing:0.8, marginBottom:10,
                textTransform:"uppercase",
              }}>
                {LOAD_STEPS[step]}
              </div>

              {/* Progress track */}
              <div style={{
                position:"relative", height:10, borderRadius:99,
                background:"rgba(255,255,255,0.15)",
                maxWidth:500, margin:"0 auto",
              }}>
                {/* Fill bar */}
                <div style={{
                  position:"absolute", left:0, top:0, bottom:0, borderRadius:99,
                  background:"linear-gradient(90deg,#6366F1,#8B5CF6,#A78BFA)",
                  transition:"width 1s ease",
                  width:`${progress}%`,
                  boxShadow:"0 0 10px rgba(139,92,246,0.7)",
                }} />

                {/* Mini truck on progress bar */}
                <div style={{
                  position:"absolute", top:"50%",
                  left:`calc(${progress}% - 20px)`,
                  transform:"translateY(-50%)",
                  transition:"left 1s ease",
                }}>
                  <svg viewBox="0 0 40 18" width="40" height="18">
                    <rect x="0"  y="4"  width="26" height="11" rx="2" fill="#6366F1" />
                    <rect x="26" y="6"  width="12" height="9"  rx="2" fill="#4338CA" />
                    <path d="M27,6 Q29,2 33,2 L37,2 L38,6 Z" fill="#312E81" />
                    <rect x="36" y="7" width="3" height="3" rx="0.5" fill="#FEF3C7" />
                    <circle cx="6"  cy="15" r="3" fill="#1E1B4B" />
                    <circle cx="6"  cy="15" r="1.5" fill="#6366F1" />
                    <circle cx="18" cy="15" r="3" fill="#1E1B4B" />
                    <circle cx="18" cy="15" r="1.5" fill="#6366F1" />
                    <circle cx="32" cy="15" r="3" fill="#1E1B4B" />
                    <circle cx="32" cy="15" r="1.5" fill="#6366F1" />
                  </svg>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
}
